/**
 * API Client for SagaFe Backend
 * Centralized API handling with token management and error handling
 */

// API Base URL - uses environment variable or falls back to localhost
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Makes an authenticated API request
 * Automatically handles token attachment and 401 responses
 */
async function apiRequest(endpoint, options = {}) {
  const token = localStorage.getItem('access_token');

  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized
  if (response.status === 401) {
    const error = await response.json().catch(() => ({ detail: 'Unauthorized' }));
    
    // ✅ Check if this is a login attempt
    const isLoginAttempt = endpoint === '/auth/login';

    if (token && headers['Authorization'] && !isLoginAttempt) {
      // This was an authenticated request that failed - session expired
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth-expired'));
      throw new Error('Session expired. Please log in again.');
    }
    
    // ✅ For login failures, throw the actual error message
    const detail = typeof error.detail === 'string' ? error.detail
      : Array.isArray(error.detail) ? error.detail.map(e => e.msg || JSON.stringify(e)).join(', ')
      : 'Invalid email or password';
    throw new Error(detail);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    console.error('API error response:', response.status, JSON.stringify(error));
    let message = 'Request failed';
    if (typeof error.detail === 'string') {
      message = error.detail;
    } else if (Array.isArray(error.detail)) {
      // Pydantic validation errors return an array of objects with loc and msg
      message = error.detail.map(e => {
        const field = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : '';
        const msg = e.msg || 'invalid';
        return field ? `${field}: ${msg}` : msg;
      }).join(', ');
    }
    throw new Error(message);
  }

  // Handle empty responses
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

/**
 * API methods
 */
export const api = {
  /**
   * GET request
   */
  get: (endpoint) => apiRequest(endpoint, { method: 'GET' }),

  /**
   * POST request with JSON body
   */
  post: (endpoint, data) =>
    apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * PUT request with JSON body
   */
  put: (endpoint, data) =>
    apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * DELETE request
   */
  delete: (endpoint) => apiRequest(endpoint, { method: 'DELETE' }),
};

/**
 * Auth-specific API methods
 */
export const authApi = {
  /**
   * User login
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{access_token: string, token_type: string, user: object}>}
   */
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });

    // Store token and user data
    if (response.access_token) {
      localStorage.setItem('access_token', response.access_token);
    }
    if (response.user) {
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  },

  /**
   * User registration
   * @param {object} userData - { first_name, last_name, email, password, phone_number?, golf_handicap? }
   * @returns {Promise<{message: string, user: object}>}
   */
  signup: async (userData) => {
    return api.post('/auth/signup', userData);
  },

  /**
   * User logout - invalidates token on server
   */
  logout: async () => {
    try {
      await api.post('/auth/logout', {});
    } catch (error) {
      // Ignore logout errors - still clear local storage
      console.warn('Logout API call failed:', error.message);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
  },

  /**
   * Get stored user from localStorage
   */
  getStoredUser: () => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  },

  /**
   * Request password reset
   * @param {string} email - User's email address
   * @returns {Promise<{message: string}>}
   */
  forgotPassword: async (email) => {
    return api.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} new_password - New password
   * @returns {Promise<{message: string}>}
   */
  resetPassword: async (token, new_password) => {
    return api.post('/auth/reset-password', { token, new_password });
  },
  
  /* Grabs the data associated with the logged-in user*/
  getMe: async () => {
    return api.get('/api/users/me');
  },

};

/**
 * Events API methods
 */
export const eventsApi = {
  /**
   * Get all events
   * @returns {Promise<Array<{id: number, township: string, golf_course: string, date: date, capacity: number}>>}
   */
  getAll: async () => {
    return api.get('/api/events/');
  },
};


/**
 * Banner Messages API methods
 */
export const bannerApi = {
  /**
   * Get all events
   * @returns {Promise<Array<{id: number, message: string}>>}
   */
  getAll: async () => {
    return api.get('/api/banner_messages/');
  },
};

/**
 * Payments API methods
 */
export const paymentsApi = {
  /**
   * Get payment history for the authenticated user
   * @param {object} params - { payment_type, status, limit, offset }
   * @returns {Promise<{payments: Array, total: number}>}
   */
  getHistory: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.payment_type) query.set('payment_type', params.payment_type);
    if (params.status) query.set('status', params.status);
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);
    const qs = query.toString();
    return api.get(`/api/payments/history${qs ? `?${qs}` : ''}`);
  },
};

/**
 * Registrations API methods (with payment)
 */
export const registrationsApi = {
  /**
   * Register an authenticated member for an event (with payment)
   * @param {object} data - { event_id, handicap, price_tier_id, payment_token, idempotency_key }
   */
  register: async (data) => {
    return api.post('/api/registrations', data);
  },

  /**
   * Retry a failed payment for an existing registration
   * @param {number} registrationId
   * @param {object} data - { payment_token, idempotency_key }
   */
  retryPayment: async (registrationId, data) => {
    return api.post(`/api/registrations/${registrationId}/retry-payment`, data);
  },

  /**
   * Register a guest for an event (no auth required)
   * @param {object} data - { event_id, first_name, last_name, email, phone, handicap, referral_source, payment_token, idempotency_key }
   */
  registerGuest: async (data) => {
    return api.post('/api/registrations/guest', data);
  },
};

/**
 * Memberships API methods
 */
export const membershipsApi = {
  /**
   * Get available membership tiers (active only)
   */
  getTiers: async () => {
    return api.get('/api/memberships/tiers');
  },

  /**
   * Get current user's membership status
   */
  getStatus: async () => {
    return api.get('/api/memberships/status');
  },

  /**
   * Pay for a membership
   * @param {object} data - { tier_id, payment_token, idempotency_key }
   */
  pay: async (data) => {
    return api.post('/api/memberships/pay', data);
  },
};

/**
 * Admin API methods
 */
export const adminApi = {
  /**
   * Get guest event rate
   */
  getGuestRate: async () => {
    return api.get('/api/admin/settings/guest-event-rate');
  },

  /**
   * Update guest event rate
   * @param {number} rate
   */
  updateGuestRate: async (rate) => {
    return api.put('/api/admin/settings/guest-event-rate', { rate });
  },

  /**
   * Process a refund
   * @param {number} paymentId
   */
  refundPayment: async (paymentId) => {
    return api.post(`/api/payments/${paymentId}/refund`);
  },

  /**
   * Mark an event registration as paid
   * @param {number} registrationId
   * @param {object} data - { amount, note }
   */
  markRegistrationPaid: async (registrationId, data) => {
    return api.post(`/api/registrations/${registrationId}/mark-paid`, data);
  },

  /**
   * Mark a membership as paid
   * @param {number} userId
   * @param {object} data - { tier_id, note }
   */
  markMembershipPaid: async (userId, data) => {
    return api.post(`/api/memberships/${userId}/mark-paid`, data);
  },

  /**
   * Get all membership tiers (including inactive)
   */
  getMembershipTiers: async () => {
    return api.get('/api/admin/membership-tiers');
  },

  /**
   * Create a membership tier
   * @param {object} data - { name, amount, description, sort_order }
   */
  createMembershipTier: async (data) => {
    return api.post('/api/admin/membership-tiers', data);
  },

  /**
   * Update a membership tier
   * @param {number} tierId
   * @param {object} data - { name, amount, description, sort_order, is_active }
   */
  updateMembershipTier: async (tierId, data) => {
    return api.put(`/api/admin/membership-tiers/${tierId}`, data);
  },

  /**
   * Deactivate a membership tier
   * @param {number} tierId
   */
  deactivateMembershipTier: async (tierId) => {
    return api.delete(`/api/admin/membership-tiers/${tierId}`);
  },
};

/**
 * Users API methods (for admin)
 */
export const usersApi = {
  /**
   * Get all users (admin only)
   * @returns {Promise<Array<User>>}
   */
  getAll: async () => {
    return api.get('/api/admin/users');
  },

  /**
   * Update user role (admin only)
   * @param {number} userId
   * @param {string} role
   */
  updateRole: async (userId, role) => {
    return api.put(`/api/admin/users/${userId}/role`, { role });
  },

  /**
   * Create a new user (admin only) — sends password setup email to the new user
   * @param {object} userData
   */
  create: async (userData) => {
    return api.post('/api/admin/users', userData);
  },

  /**
   * Delete user (admin only)
   * @param {number} userId
   */
  delete: async (userId) => {
    return api.delete(`/api/admin/users/${userId}`);
  },

  toggleMembershipExempt: async (userId) => {
    return api.put(`/api/admin/users/${userId}/membership-exempt`);
  },

  deleteEventRegistration: async (registrationId) => {
    return api.delete(`/api/admin/event-registrations/${registrationId}`);
  },

  /**
   * Get event registrations for a specific event (admin only)
   * @param {number} eventId
   */
  getEventRegistrations: async (eventId) => {
    const response = await api.get(`/api/admin/events/${eventId}/registrations`);
    return response.registrations;
  },
};

/**
 * Admin Events API methods
 */
export const adminEventsApi = {
  /**
   * Create new event (admin only)
   * @param {object} eventData
   */
  create: async (eventData) => {
    return api.post('/api/admin/events', eventData);
  },

  /**
   * Update event (admin only)
   * @param {number} eventId
   * @param {object} eventData
   */
  update: async (eventId, eventData) => {
    return api.put(`/api/admin/events/${eventId}`, eventData);
  },

  toggleRegistration: async (eventId, registrationOpen) => {
    return api.put(`/api/admin/events/${eventId}`, { registration_open: registrationOpen });
  },

  /**
   * Delete event (admin only)
   * @param {number} eventId
   */
  delete: async (eventId) => {
    return api.delete(`/api/admin/events/${eventId}`);
  },

  getAll: async (orderBy = 'date', order = 'asc') => {
    return api.get(`/api/admin/events?order_by=${orderBy}&order=${order}`);
  },
};

/**
 * Admin Banner API methods
 */
export const adminBannerApi = {
  /**
   * Update banner messages (admin only)
   * @param {Array<{id?: number, message: string}>} messages
   */
  updateMessages: async (messages) => {
    return api.put('/api/banner_messages/messages', { messages });
  },

  /**
   * Update banner display count (admin only)
   * @param {number} count
   */
  updateDisplayCount: async (count) => {
    return api.put(`/api/banner_messages/display-count?count=${count}`);
  },
};

/**
 * Admin Photos API methods
 */
export const adminPhotosApi = {
  /**
   * Get all photo albums (admin only)
   */
  getAll: async () => {
    return api.get('/api/admin/photo-albums');
  },

  /**
   * Create photo album (admin only)
   * @param {object} albumData
   */
  create: async (albumData) => {
    return api.post('/api/admin/photo-albums', albumData);
  },

  /**
   * Update photo album (admin only)
   * @param {number} albumId
   * @param {object} albumData
   */
  update: async (albumId, albumData) => {
    return api.put(`/api/admin/photo-albums/${albumId}`, albumData);
  },

  /**
   * Delete photo album (admin only)
   * @param {number} albumId
   */
  delete: async (albumId) => {
    return api.delete(`/api/admin/photo-albums/${albumId}`);
  },
};

/**
 * Photos API methods (public)
 */
export const photosApi = {
  /**
   * Get all photo albums
   * @returns {Promise<Array<PhotoAlbum>>}
   */
  getAll: async () => {
    return api.get('/api/photo-albums');
  },
};

/**
 * Admin Content API methods
 */
export const adminContentApi = {
  /**
   * Get site content/prompts (admin only)
   */
  getContent: async () => {
    return api.get('/api/admin/content');
  },

  /**
   * Update site content/prompts (admin only)
   * @param {object} content
   */
  updateContent: async (content) => {
    return api.put('/api/admin/content', content);
  },
};

/**
 * Admin Media API methods
 */
export const adminMediaApi = {
  /**
   * Upload image (admin only)
   * @param {FormData} formData - Must contain 'file'
   */
  uploadImage: async (formData) => {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_URL}/api/admin/media/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData, // Don't set Content-Type for FormData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  },

  /**
   * Get carousel images (admin only)
   */
  getCarouselImages: async () => {
    return api.get('/api/admin/media/carousel');
  },

  /**
   * Update carousel images (admin only)
   * @param {Array<string>} imageUrls
   */
  updateCarousel: async (imageUrls) => {
    const cleanUrls = imageUrls.map(url => 
      typeof url === 'string' ? url : url.url || url.image_url || String(url)
    );
    return api.put('/api/admin/media/carousel', { images: cleanUrls });
  },
};

/**
 * Carousel API (public)
 */
export const carouselApi = {
  getImages: async () => {
    const response = await api.get('/api/carousel');
    return response.images || [];
  },
};

/**
 * Partners API (public)
 */
export const partnersApi = {
  getAll: async () => {
    return api.get('/api/partners');
  },
};

/**
 * Admin Partners API
 */
export const exemptionCodesApi = {
  validate: async (code) => {
    return api.post('/auth/validate-exemption-code', { code });
  },
  getAll: async () => {
    const response = await api.get('/api/admin/exemption-codes');
    return response.codes || [];
  },
  create: async (data) => {
    return api.post('/api/admin/exemption-codes', data);
  },
  delete: async (codeId) => {
    return api.delete(`/api/admin/exemption-codes/${codeId}`);
  },
};

export const eventPromoCodesApi = {
  validate: async (code, eventId) => {
    return api.post('/api/registrations/validate-promo-code', { code, event_id: eventId });
  },
  getAll: async () => {
    const response = await api.get('/api/admin/event-promo-codes');
    return response.codes || [];
  },
  create: async (data) => {
    return api.post('/api/admin/event-promo-codes', data);
  },
  delete: async (codeId) => {
    return api.delete(`/api/admin/event-promo-codes/${codeId}`);
  },
};

export const adminPartnersApi = {
  getAll: async () => {
    return api.get('/api/admin/partners');
  },

  create: async (partnerData) => {
    return api.post('/api/admin/partners', partnerData);
  },

  update: async (partnerId, partnerData) => {
    return api.put(`/api/admin/partners/${partnerId}`, partnerData);
  },

  delete: async (partnerId) => {
    return api.delete(`/api/admin/partners/${partnerId}`);
  },
};

/**
 * News API (public)
 */
export const newsApi = {
  getAll: async () => {
    const response = await api.get('/api/news');
    return response.news_items || [];
  },
};

/**
 * Admin News API
 */
export const adminNewsApi = {
  getAll: async () => {
    const response = await api.get('/api/admin/news');
    return response.news_items || [];
  },

  fetchMetadata: async (url) => {
    return api.post('/api/admin/news/fetch-metadata', { url });
  },

  create: async (newsData) => {
    return api.post('/api/admin/news', newsData);
  },

  update: async (newsId, newsData) => {
    return api.put(`/api/admin/news/${newsId}`, newsData);
  },

  delete: async (newsId) => {
    return api.delete(`/api/admin/news/${newsId}`);
  },
};

export const faqApi = {
  /**
   * Get all active FAQs (public endpoint)
   */
  getAll: async () => {
    return api.get('/api/faqs');
  },

  /**
   * Get all FAQs including inactive (admin only)
   */
  getAllAdmin: async () => {
    return api.get('/api/admin/faqs');
  },

  /**
   * Create a new FAQ (admin only)
   */
  create: async (faqData) => {
    return api.post('/api/admin/faqs', faqData);
  },

  /**
   * Update an existing FAQ (admin only)
   */
  update: async (id, faqData) => {
    return api.put(`/api/admin/faqs/${id}`, faqData);
  },

  /**
   * Delete an FAQ (admin only)
   */
  delete: async (id) => {
    return api.delete(`/api/admin/faqs/${id}`);
  },
};

export const scholarshipRecipientsApi = {
  /**
   * Get all scholarship recipients (public endpoint)
   */
  getAll: async () => {
    return api.get('/api/scholarship-recipients');
  },

  /**
   * Get scholarship recipients by year (public endpoint)
   */
  getByYear: async (year) => {
    return api.get(`/api/scholarship-recipients/by-year/${year}`);
  },

  /**
   * Get all recipients including admin data (admin only)
   */
  getAllAdmin: async () => {
    return api.get('/api/admin/scholarship-recipients');
  },

  /**
   * Create a new scholarship recipient (admin only)
   */
  create: async (recipientData) => {
    return api.post('/api/admin/scholarship-recipients', recipientData);
  },

  /**
   * Update an existing scholarship recipient (admin only)
   */
  update: async (id, recipientData) => {
    return api.put(`/api/admin/scholarship-recipients/${id}`, recipientData);
  },

  /**
   * Delete a scholarship recipient (admin only)
   */
  delete: async (id) => {
    return api.delete(`/api/admin/scholarship-recipients/${id}`);
  },
};

export const membershipOptionsApi = {
  /**
   * Get all active membership options (public endpoint)
   */
  getAll: async () => {
    return api.get('/api/membership-options');
  },

  /**
   * Get all membership options including inactive (admin only)
   */
  getAllAdmin: async () => {
    return api.get('/api/admin/membership-options');
  },

  /**
   * Create a new membership option (admin only)
   */
  create: async (optionData) => {
    return api.post('/api/admin/membership-options', optionData);
  },

  /**
   * Update an existing membership option (admin only)
   */
  update: async (id, optionData) => {
    return api.put(`/api/admin/membership-options/${id}`, optionData);
  },

  /**
   * Delete a membership option (admin only)
   */
  delete: async (id) => {
    return api.delete(`/api/admin/membership-options/${id}`);
  },
};

/**
 * Past Champions API methods
 */
export const pastChampionsApi = {
  /**
   * Get all past champions (public endpoint, ordered by year desc)
   */
  getAll: async () => {
    return api.get('/api/past-champions');
  },

  /**
   * Get all past champions with full details (admin only)
   */
  getAllAdmin: async () => {
    return api.get('/api/admin/past-champions');
  },

  /**
   * Create a new past champion (admin only)
   * @param {object} data - { name, year }
   */
  create: async (data) => {
    return api.post('/api/admin/past-champions', data);
  },

  /**
   * Update an existing past champion (admin only)
   * @param {number} id
   * @param {object} data - { name?, year? }
   */
  update: async (id, data) => {
    return api.put(`/api/admin/past-champions/${id}`, data);
  },

  /**
   * Delete a past champion (admin only)
   * @param {number} id
   */
  delete: async (id) => {
    return api.delete(`/api/admin/past-champions/${id}`);
  },
};

/**
 * Members API methods (for member search)
 */
export const membersApi = {
  /**
   * Search members by name (requires authentication)
   * @param {string} query - Search term
   * @returns {Promise<Array<{user_id, first_name, last_name, email, phone, handicap}>>}
   */
  search: async (query) => {
    return api.get(`/api/users/members/search?q=${encodeURIComponent(query)}`);
  },
};

/**
 * Health check
 */
export const healthCheck = async () => {
  try {
    const response = await api.get('/health');
    return response.status === 'healthy';
  } catch {
    return false;
  }
};

/**
 * PayPal API methods
 */
export const paypalApi = {
  createOrder: async (amount, description = 'SAGA Golf Payment') => {
    return api.post('/api/paypal/create-order', { amount, description });
  },
};

export default api;